import * as React from "react";
import { cn } from "../../lib/utils";
import { HeroBanner } from "../HeroBanner";
import { PromoBanner } from "../PromoBanner";
import { PackagesGrid } from "../PackagesGrid";
import { DestinationsShowcase } from "../DestinationsShowcase";
import { OffersSearchWidget } from "../OffersSearchWidget";
import { AppDownloadBanner } from "../AppDownloadBanner";
import { StatsBar } from "../StatsBar";
import { TestimonialsSection } from "../TestimonialsSection";
import { FaqSection } from "../FaqSection";
import { NewsletterSignup } from "../NewsletterSignup";
import { TrustBadges } from "../TrustBadges";
import { ImageGallery } from "../ImageGallery";
import { PriceTrendsBars } from "../PriceTrendsBars";
import { PriceTrendsHeatmapStrip } from "../PriceTrendsHeatmapStrip";
import { PriceTrendsHeatmapGrid } from "../PriceTrendsHeatmapGrid";
import type { PlacePageBuilderProps, PlaceSection } from "./types";

const FULL_BLEED_TYPES = new Set<PlaceSection["type"]>(["hero"]);

function renderSection(
  section: PlaceSection,
  sectionRenderers?: PlacePageBuilderProps["sectionRenderers"]
): React.ReactNode {
  const customRenderer = sectionRenderers?.[section.type];
  if (customRenderer) {
    return customRenderer(section);
  }

  switch (section.type) {
    case "hero":
      return <HeroBanner key={section.id} {...section.data} />;
    case "promo-banner":
      return <PromoBanner key={section.id} {...section.data} />;
    case "packages-grid":
      return <PackagesGrid key={section.id} {...section.data} />;
    case "destinations-showcase":
      return <DestinationsShowcase key={section.id} {...section.data} />;
    case "offers-search":
      return <OffersSearchWidget key={section.id} {...section.data} />;
    case "app-download":
      return <AppDownloadBanner key={section.id} {...section.data} />;
    case "stats-bar":
      return <StatsBar key={section.id} {...section.data} />;
    case "testimonials":
      return <TestimonialsSection key={section.id} {...section.data} />;
    case "faq":
      return <FaqSection key={section.id} {...section.data} />;
    case "newsletter":
      return <NewsletterSignup key={section.id} {...section.data} />;
    case "trust-badges":
      return <TrustBadges key={section.id} {...section.data} />;
    case "image-gallery":
      return <ImageGallery key={section.id} {...section.data} />;
    case "price-trends-bars":
      return <PriceTrendsBars key={section.id} {...section.data} />;
    case "price-trends-heatmap-strip":
      return <PriceTrendsHeatmapStrip key={section.id} {...section.data} />;
    case "price-trends-heatmap-grid":
      return <PriceTrendsHeatmapGrid key={section.id} {...section.data} />;
    // Shared components need the app's currency and analytics, so the app supplies the renderer
    // through sectionRenderers; without one there is nothing sensible to draw.
    case "shared-component":
      return null;
    default: {
      const _exhaustive: never = section;
      return _exhaustive;
    }
  }
}

const DEFAULT_CONTAINER =
  "mx-auto w-full min-w-0 max-w-7xl px-4 md:px-8 flex flex-col gap-8 py-8 md:gap-10 md:py-10";

export const PlacePageBuilder = React.forwardRef<
  HTMLDivElement,
  PlacePageBuilderProps
>(({ className, sections, containerClassName, sectionRenderers, ...props }, ref) => {
  const groups: Array<{
    type: "full-bleed" | "contained";
    items: PlaceSection[];
  }> = [];

  for (const section of sections) {
    const groupType: "full-bleed" | "contained" = FULL_BLEED_TYPES.has(
      section.type
    )
      ? "full-bleed"
      : "contained";
    const last = groups[groups.length - 1];
    if (last && last.type === groupType) {
      last.items.push(section);
    } else {
      groups.push({ type: groupType, items: [section] });
    }
  }

  return (
    <div ref={ref} className={cn("w-full min-w-0", className)} {...props}>
      {groups.map((group, idx) => {
        if (group.type === "full-bleed") {
          return (
            <React.Fragment key={`full-${idx}`}>
              {group.items.map((section) =>
                renderSection(section, sectionRenderers)
              )}
            </React.Fragment>
          );
        }
        return (
          <div
            key={`contained-${idx}`}
            className={cn(DEFAULT_CONTAINER, containerClassName)}
          >
            {group.items.map((section) =>
              renderSection(section, sectionRenderers)
            )}
          </div>
        );
      })}
    </div>
  );
});

PlacePageBuilder.displayName = "PlacePageBuilder";
